/**
 * saas-auth product-admin identity DTOs (WSRPDM-0018).
 *
 * Every shape maps VERBATIM to
 * docs/design/HANDOFF-saas-auth-tenant-detail-invitations.md (§1 detail, §2
 * invitations, §5 create, §6 members). saas-auth wraps every response in the
 * `{success,message,data}` ApiResponse envelope (contrast wsr-api's raw DTOs
 * in `../wsr/types.ts`) — every call in `./tenants.ts` calls `unwrap()`.
 */

export type TenantStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
export type SignupMode = 'PUBLIC' | 'INVITE_ONLY' | 'ADMIN_CREATED_ONLY' | 'DISABLED';

/** §1 — `GET /tenants/{tenantId}`; also the §5 create response (201). */
export interface TenantDto {
  id: number;
  slug: string;
  name: string;
  status: TenantStatus;
  signupMode: SignupMode;
  /** Present and `null` when unset — NEVER omitted (contract §1.1). Nullable, not
   *  optional: the key is always on the wire. WSRPDM-0059 — declaring these `?: string`
   *  made a null-unsafe guard typecheck as exhaustive and crashed the whole Settings tab. */
  emailFromName: string | null;
  emailFromAddress: string | null;
  emailSupportEmail: string | null;
  /** Omitted when null (contract §1). */
  productId?: number;
  brandId?: number;
  planId?: number;
  createdAt: string;
}

/** §5 — `POST /tenants` request body. Product is forced server-side from the token. */
export interface CreateTenantRequest {
  slug: string;
  name: string;
  signupMode: SignupMode;
}

/**
 * Member role enum. `GET …/users` is the customer list (`listMembers`);
 * this console’s Staff tab uses `GET …/staff` (`listStaff`). `GUEST` is
 * roster-only and filtered out of `listMembers` — never rendered here.
 */
export type TenantMemberRole = 'OWNER' | 'SUPER_ADMIN' | 'ADMIN' | 'STAFF' | 'USER' | 'GUEST';

/** Roles assignable via `addMember`/`changeRole` — staff ladder only. Never `USER`. */
export type AssignableRole = 'OWNER' | 'ADMIN' | 'STAFF';

export const STAFF_ROLES = ['OWNER', 'ADMIN', 'STAFF'] as const;

export function isStaffRole(role: string): role is AssignableRole {
  return (STAFF_ROLES as readonly string[]).includes(role);
}

export type TenantMemberStatus = 'ACTIVE' | 'INVITED' | 'DISABLED';

/**
 * One row of `GET /tenants/{tenantId}/staff` (and the unused customer
 * `GET …/users`). Mutations key on `membershipId`, NOT userId.
 */
export interface TenantMemberDto {
  membershipId: number;
  email: string;
  name?: string;
  role: TenantMemberRole;
  status: TenantMemberStatus;
}

export interface AddMemberRequest {
  email: string;
  role: AssignableRole;
}

/** §2 — invitation status is server-COMPUTED; render as the badge as-is, never recompute it. */
export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

/** §2 — one row of `GET /tenants/{tenantId}/invitations` (a JSON array, not paged). */
export interface TenantInvitationDto {
  id: number;
  email: string;
  role: TenantMemberRole;
  oidcClientIds: number[];
  status: InvitationStatus;
  tenantId: number;
  invitedByUserId: number;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
}

/**
 * §6 — `POST /tenants/{tenantId}/users` discriminated result: an existing
 * account is added directly as a member (201), or an unknown email is
 * invited instead (202, appears in the invitations list). See
 * `./tenants.ts#addMember` for how this is discriminated.
 */
export type AddMemberResult =
  | { kind: 'member'; member: TenantMemberDto }
  | { kind: 'invitation'; invitation: TenantInvitationDto };
